<template>
  <vs-popup
    title="Compartilhamentos"
    :active.sync="isVisible"
    class="sharing-details-popup"
  >
    <div class="sharing-content">
      <div v-if="loadingShares" class="status-row">
        Carregando compartilhamentos...
      </div>
      <div v-else>
        <div v-if="isOwner" class="owner-indicator">
          Você é o proprietário deste recurso.
        </div>
        <div v-if="sharesError" class="status-row error">
          Ocorreu um erro ao carregar os compartilhamentos.
        </div>
        <div v-else-if="hasShares" class="sharing-section">
          <div v-if="sharedUsers.length" class="sharing-block">
            <h4 class="sharing-title">Usuários</h4>
            <ul class="sharing-list">
              <li v-for="user in sharedUsers" :key="`user-${user.id}`">
                <span class="primary">{{ user.name }}</span>
                <span class="secondary">ID usuário: {{ user.id }}</span>
                <span v-if="user.email" class="secondary">{{
                  user.email
                }}</span>
                <span v-if="isCurrentUser(user)" class="badge-self">{{
                  isOwner ? 'Você (proprietário)' : 'Você'
                }}</span>
              </li>
            </ul>
          </div>

          <div v-if="sharedGroups.length" class="sharing-block">
            <h4 class="sharing-title">Grupos</h4>
            <ul class="sharing-list">
              <li v-for="group in sharedGroups" :key="`group-${group.id}`">
                <span class="primary">{{
                  group.display_name || group.name
                }}</span>
                <span class="secondary">ID grupo: {{ group.id }}</span>
              </li>
            </ul>
          </div>
        </div>

        <div v-else class="status-row empty">
          Nenhum compartilhamento encontrado para este recurso.
        </div>
      </div>
    </div>

    <div class="footer-dialog justify-end">
      <b-button variant="outline-secondary" @click="close">
        <feather-icon icon="XIcon" size="16" class="mr-1" />
        Fechar
      </b-button>
    </div>
  </vs-popup>
</template>

<script>
import { BButton } from 'bootstrap-vue'
import FeatherIcon from '@/components/FeatherIcon.vue'
import DriveService from '@/services/api/DriveService'

export default {
  name: 'SharingDetailsPopup',
  components: {
    BButton,
    FeatherIcon
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      sharedUsers: [],
      sharedGroups: [],
      loadingShares: false,
      sharesError: null,
      driveService: null,
      lastLoadedKey: null
    }
  },
  computed: {
    isVisible: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      }
    },
    hasShares() {
      return this.sharedUsers.length > 0 || this.sharedGroups.length > 0
    },
    currentUser() {
      const userInfoStr = localStorage.getItem('userInfo')
      const userInfoObject = userInfoStr ? JSON.parse(userInfoStr) : null
      return userInfoObject
    },
    isOwner() {
      return this.item?.is_owner || false
    }
  },
  watch: {
    isVisible(newValue) {
      if (newValue) {
        this.loadSharingInfo()
      } else {
        this.resetSharing()
      }
    },
    item() {
      if (this.isVisible) {
        this.loadSharingInfo()
      } else {
        this.resetSharing()
      }
    }
  },
  created() {
    this.driveService = DriveService.build(this.$vs)
  },
  methods: {
    close() {
      this.$emit('input', false)
    },

    resetSharing() {
      this.sharedUsers = []
      this.sharedGroups = []
      this.loadingShares = false
      this.sharesError = null
      this.lastLoadedKey = null
    },

    async loadSharingInfo() {
      if (!this.item) {
        this.resetSharing()
        return
      }

      const { resource_id: resourceId, media_id: mediaId, type } = this.item

      if (!resourceId && !mediaId) {
        this.resetSharing()
        return
      }

      const requestKey = `${type || ''}-${resourceId || ''}-${mediaId || ''}`

      if (requestKey === this.lastLoadedKey && !this.sharesError) {
        return
      }

      this.loadingShares = true
      this.sharesError = null
      this.lastLoadedKey = requestKey

      try {
        const response = await this.driveService.getSharedResourcesFor({
          resource_id: resourceId,
          media_id: mediaId,
          type
        })

        const data =
          response && response.data ? response.data : { users: [], groups: [] }
        this.sharedUsers = Array.isArray(data.users) ? data.users : []
        this.sharedGroups = Array.isArray(data.groups) ? data.groups : []
      } catch (error) {
        console.error('Erro ao carregar compartilhamentos do recurso:', error)
        this.sharesError = error
        this.sharedUsers = []
        this.sharedGroups = []
      } finally {
        this.loadingShares = false
      }
    },

    isCurrentUser(entity) {
      if (!entity || !this.currentUser) return false
      const entityId = Number(entity.id)
      const currentUserId = Number(this.currentUser.id)
      if (Number.isNaN(entityId) || Number.isNaN(currentUserId)) return false
      return entityId === currentUserId
    }
  }
}
</script>

<style lang="scss" scoped>
.sharing-details-popup {
  .sharing-content {
    padding: 1rem;
  }

  .sharing-section {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .owner-indicator {
    margin-bottom: 1rem;
    padding: 0.75rem 1rem;
    border-radius: 6px;
    background-color: rgba(56, 161, 105, 0.12);
    color: #2f855a;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .sharing-block {
    padding: 1rem;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background-color: #fafafa;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  .sharing-title {
    margin: 0 0 0.75rem;
    font-size: 1rem;
    font-weight: 600;
    color: #343a40;
  }

  .sharing-list {
    margin: 0;
    padding-left: 1.2rem;
    list-style-type: disc;

    li {
      display: flex;
      flex-direction: column;
      margin-bottom: 0.35rem;
      padding: 0.4rem 0.5rem;
      border-radius: 6px;
      transition: background-color 0.2s ease;

      &:hover {
        background-color: rgba(52, 58, 64, 0.05);
      }
    }

    .primary {
      font-weight: 600;
      color: #212529;
    }

    .secondary {
      font-size: 0.85rem;
      color: #6c757d;
    }

    .badge-self {
      margin-top: 0.4rem;
      align-self: flex-start;
      font-size: 0.75rem;
      font-weight: 600;
      color: #1a202c;
      background-color: rgba(66, 153, 225, 0.18);
      border-radius: 999px;
      padding: 0.2rem 0.6rem;
      text-transform: uppercase;
      letter-spacing: 0.02em;
    }
  }

  .status-row {
    font-size: 0.95rem;
    color: #495057;
    padding: 1rem;
    text-align: center;

    &.empty {
      color: #6c757d;
      font-style: italic;
    }

    &.error {
      color: #c53030;
      font-weight: 600;
    }
  }

  .footer-dialog {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 1rem;
    border-top: 1px solid #eee;
  }
}
</style>
